import React, { useState } from "react";
import { useProfileStore } from "../../store/profileStore";
import { generateId } from "../../utils";
import { showToast } from "../shared/Toast";
import type { Profile } from "../../types";

const ProfileSection: React.FC = () => {
  const { profiles, removeProfile, updateProfile } = useProfileStore();
  const [editId, setEditId] = useState<string | null | "new">(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectMode, setSelectMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showFormer, setShowFormer] = useState(false);

  const filteredProfiles = (role: "driver" | "sideman") => {
    const q = searchQuery.toLowerCase().trim();
    return profiles.filter((p) => {
      if (p.role !== role) return false;
      if ((p as any).isFormerEmployee) return false;
      if (!q) return true;
      const fmtD = (raw: string | undefined) => {
        if (!raw) return "";
        if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
          const [y, m, d] = raw.split("-");
          return `${d}/${m}/${y}`;
        }
        return raw;
      };
      return (
        ((p as any).username || "").toLowerCase().includes(q) ||
        (p.firstName || "").toLowerCase().includes(q) ||
        (p.lastName || "").toLowerCase().includes(q) ||
        (p.age || "").toLowerCase().includes(q) ||
        fmtD(p.dob).includes(q) ||
        (p.phone || "").toLowerCase().includes(q)
      );
    });
  };

  const formerProfiles = () => {
    const q = searchQuery.toLowerCase().trim();
    return profiles.filter((p) => {
      if (!(p as any).isFormerEmployee) return false;
      if (!q) return true;
      const fmtD = (raw: string | undefined) => {
        if (!raw) return "";
        if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
          const [y, m, d] = raw.split("-");
          return `${d}/${m}/${y}`;
        }
        return raw;
      };
      return (
        ((p as any).username || "").toLowerCase().includes(q) ||
        (p.firstName || "").toLowerCase().includes(q) ||
        (p.lastName || "").toLowerCase().includes(q) ||
        fmtD(p.dob).includes(q)
      );
    });
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const deleteSelected = () => {
    if (
      !confirm(
        `Mark ${selected.size} profile${selected.size !== 1 ? "s" : ""} as former employee${selected.size !== 1 ? "s" : ""}? They'll be removed from active routes but historical records remain intact.`,
      )
    )
      return;
    selected.forEach((id) =>
      updateProfile(id, (existing: any) => ({
        ...existing,
        isFormerEmployee: true,
        deletedAt: new Date().toISOString(),
      })),
    );
    setSelected(new Set());
    setSelectMode(false);
    showToast(
      `${selected.size} profile${selected.size !== 1 ? "s" : ""} marked as former employee${selected.size !== 1 ? "s" : ""}`,
    );
  };

  const fmtDob = (raw: string | undefined): string => {
    if (!raw) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [y, m, d] = raw.split("-");
      return `${d}/${m}/${y}`;
    }
    return raw; // already DD/MM/YYYY or other
  };

  return (
    <>
      <div className="prof-section" id="prof-section">
        <div className="prof-header">
          <div className="prof-title">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path
                d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <circle
                cx="9"
                cy="7"
                r="4"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path
                d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            Crew Profiles
          </div>
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <button className="prof-add-btn" onClick={() => setEditId("new")}>
              + Add Profile
            </button>
            <button
              className="prof-add-btn"
              style={{
                background: selectMode ? "rgba(232,85,32,0.15)" : "transparent",
                border: "1px solid rgba(232,85,32,0.4)",
                color: "#E85520",
              }}
              onClick={() => {
                setSelectMode((o) => !o);
                setSelected(new Set());
              }}
            >
              {selectMode ? "Cancel" : "Select"}
            </button>
            {selectMode && (
              <>
                <button
                  className="prof-add-btn"
                  style={{
                    background: "transparent",
                    border: "1px solid rgba(255,255,255,0.15)",
                    color: "#7a7d85",
                  }}
                  onClick={() =>
                    setSelected(new Set(profiles.map((p) => p.id)))
                  }
                >
                  Select All
                </button>
                {selected.size > 0 && (
                  <button
                    className="prof-add-btn"
                    style={{
                      background: "rgba(224,82,82,0.15)",
                      border: "1px solid rgba(224,82,82,0.4)",
                      color: "#e05252",
                    }}
                    onClick={deleteSelected}
                  >
                    Delete ({selected.size})
                  </button>
                )}
              </>
            )}
            <button
              className="prof-add-btn"
              style={{
                background: "transparent",
                border: "1px solid rgba(62,207,110,0.4)",
                color: "#3ecf6e",
              }}
              onClick={() => {
                const csv = [
                  [
                    "First Name (optional)",
                    "Last Name (optional)",
                    "Username (required)",
                    "User ID (required)",
                    "Date of Birth DD/MM/YYYY (required)",
                    "Phone (optional)",
                    "Role driver or sideman (required)",
                    "Notes (optional)",
                  ],
                  [
                    "John",
                    "Smith",
                    "john_driver",
                    "EMP001",
                    "14/05/1990",
                    "07700900000",
                    "driver",
                    "",
                  ],
                  [
                    "Jane",
                    "Doe",
                    "jane_sideman",
                    "EMP002",
                    "22/08/1995",
                    "07700900001",
                    "sideman",
                    "",
                  ],
                ]
                  .map((r) =>
                    r.map((c) => '"' + c.replace(/"/g, '""') + '"').join(","),
                  )
                  .join("\n");
                const blob = new Blob([csv], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "FleetOps_Crew_Template.csv";
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              ↓ Download Template
            </button>
            <button
              className="prof-add-btn"
              style={{
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.15)",
                color: "#7a7d85",
              }}
              onClick={() => {
                const rows = [
                  [
                    "First Name",
                    "Last Name",
                    "Username",
                    "User ID",
                    "Date of Birth DD/MM/YYYY",
                    "Phone",
                    "Role",
                    "Notes",
                  ],
                ];
                profiles.forEach((p) => {
                  const fmtD = (raw: string | undefined) => {
                    if (!raw) return "";
                    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
                      const [y, m, d] = raw.split("-");
                      return `${d}/${m}/${y}`;
                    }
                    return raw;
                  };
                  rows.push([
                    p.firstName || "",
                    p.lastName || "",
                    (p as any).username || "",
                    p.age || "",
                    fmtD(p.dob),
                    p.phone || "",
                    p.role,
                    p.notes || "",
                  ]);
                });
                const csv = rows
                  .map((r) =>
                    r
                      .map((c) => '"' + String(c).replace(/"/g, '""') + '"')
                      .join(","),
                  )
                  .join("\n");
                const blob = new Blob([csv], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `FleetOps_Profiles_${new Date().toISOString().slice(0, 10)}.csv`;
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              ↓ Export Profiles
            </button>
            <button
              className="prof-add-btn"
              style={{
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.15)",
                color: "#7a7d85",
              }}
              onClick={() => {
                const rows = [
                  [
                    "First Name",
                    "Last Name",
                    "Username",
                    "User ID",
                    "Date of Birth DD/MM/YYYY",
                    "Phone",
                    "Role",
                    "Notes",
                  ],
                ];
                profiles.forEach((p) => {
                  const fmtD = (raw: string | undefined) => {
                    if (!raw) return "";
                    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
                      const [y, m, d] = raw.split("-");
                      return `${d}/${m}/${y}`;
                    }
                    return raw;
                  };
                  rows.push([
                    p.firstName || "",
                    p.lastName || "",
                    (p as any).username || "",
                    p.age || "",
                    fmtD(p.dob),
                    p.phone || "",
                    p.role,
                    p.notes || "",
                  ]);
                });
                const csv = rows
                  .map((r) =>
                    r
                      .map((c) => '"' + String(c).replace(/"/g, '""') + '"')
                      .join(","),
                  )
                  .join("\n");
                const blob = new Blob([csv], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `FleetOps_Profiles_${new Date().toISOString().slice(0, 10)}.csv`;
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              ↓ Export Profiles
            </button>
            <label style={{ cursor: "pointer" }}>
              <span
                className="prof-add-btn"
                style={{
                  background: "transparent",
                  border: "1px solid rgba(100,160,255,0.4)",
                  color: "#64a0ff",
                  display: "inline-block",
                }}
              >
                ↑ Upload CSV
              </span>
              <input
                type="file"
                accept=".csv"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = (ev) => {
                    const text = ev.target?.result as string;
                    const lines = text
                      .split("\n")
                      .map((l) => l.trim())
                      .filter(Boolean);
                    if (lines.length < 2) {
                      showToast("No data rows found in CSV");
                      return;
                    }
                    // Skip header row
                    let created = 0;
                    let skipped = 0;
                    lines.slice(1).forEach((line) => {
                      // Parse CSV row (handles quoted fields)
                      const cols: string[] = [];
                      let cur = "";
                      let inQ = false;
                      for (let i = 0; i < line.length; i++) {
                        const ch = line[i];
                        if (ch === '"') {
                          inQ = !inQ;
                        } else if (ch === "," && !inQ) {
                          cols.push(cur.trim());
                          cur = "";
                        } else {
                          cur += ch;
                        }
                      }
                      cols.push(cur.trim());
                      const firstName =
                        cols[0]?.replace(/^"|"$/g, "").trim() || "";
                      const lastName =
                        cols[1]?.replace(/^"|"$/g, "").trim() || "";
                      const username =
                        cols[2]?.replace(/^"|"$/g, "").trim() || "";
                      const userId =
                        cols[3]?.replace(/^"|"$/g, "").trim() || "";
                      let dob = cols[4]?.replace(/^"|"$/g, "").trim() || "";
                      if (dob && /^\d{2}[\/-]\d{2}[\/-]\d{4}$/.test(dob)) {
                        const dp = dob.split(/[\/-]/);
                        dob = `${dp[2]}-${dp[1]}-${dp[0]}`;
                      }
                      const phone = cols[5]?.replace(/^"|"$/g, "").trim() || "";
                      const roleRaw =
                        cols[6]?.replace(/^"|"$/g, "").trim().toLowerCase() ||
                        "";
                      const notes = cols[7]?.replace(/^"|"$/g, "").trim() || "";
                      // Validation
                      if (!username || !dob || !userId) {
                        skipped++;
                        return;
                      }
                      const role: "driver" | "sideman" =
                        roleRaw === "sideman" ? "sideman" : "driver";
                      const p = {
                        id: generateId(),
                        createdAt: new Date().toISOString(),
                        firstName,
                        lastName,
                        username,
                        role,
                        dob,
                        age: userId || null,
                        phone: phone || null,
                        notes: notes || null,
                        photo: null,
                      } as any;
                      useProfileStore.getState().addProfile(p);
                      created++;
                    });
                    showToast(
                      `${created} profile${created !== 1 ? "s" : ""} created${skipped > 0 ? `, ${skipped} skipped (missing required fields)` : ""}`,
                    );
                  };
                  reader.readAsText(file);
                  e.target.value = "";
                }}
              />
            </label>
          </div>
        </div>
        <div
          style={{
            padding: "8px 14px",
            background: "#0d0e10",
            borderBottom: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by username, name, user ID, DOB, phone…"
            style={{
              width: "100%",
              background: "#1c1e22",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 8,
              padding: "9px 14px",
              color: "#e8e9eb",
              fontFamily: "'Barlow',sans-serif",
              fontSize: 13,
              outline: "none",
              boxSizing: "border-box",
            }}
          />
        </div>

        {/* ── EMPLOYEES NO LONGER WITH THE COMPANY TAB ── */}
        <div
          style={{
            borderBottom: "1px solid rgba(224,82,82,0.22)",
            flexShrink: 0,
          }}
        >
          <button
            onClick={() => setShowFormer((o) => !o)}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 14px",
              background: showFormer
                ? "rgba(224,82,82,0.1)"
                : "rgba(224,82,82,0.04)",
              border: "none",
              cursor: "pointer",
              fontFamily: "'Barlow',sans-serif",
              transition: "background 0.2s",
            }}
          >
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 11,
                fontWeight: 800,
                textTransform: "uppercase",
                letterSpacing: "0.8px",
                color: "#e05252",
              }}
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  d="M15 9l-6 6M9 9l6 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              Employees No Longer With The Company
              <span
                style={{
                  background: "rgba(224,82,82,0.2)",
                  border: "1px solid rgba(224,82,82,0.4)",
                  borderRadius: 10,
                  padding: "1px 8px",
                  fontSize: 10,
                }}
              >
                {profiles.filter((p) => (p as any).isFormerEmployee).length}
              </span>
            </span>
            <svg
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="none"
              style={{
                color: "#e05252",
                transform: showFormer ? "rotate(180deg)" : "none",
                transition: "transform 0.2s",
              }}
            >
              <path
                d="M6 9l6 6 6-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
          {showFormer && (
            <div
              style={{
                background: "rgba(224,82,82,0.03)",
                padding: "12px 14px",
                maxHeight: 340,
                overflowY: "auto",
              }}
            >
              {profiles.filter((p) => (p as any).isFormerEmployee).length ===
                0 && (
                <div
                  style={{
                    textAlign: "center",
                    fontSize: 12,
                    color: "#7a7d85",
                    fontStyle: "italic",
                    padding: "8px 0",
                  }}
                >
                  No former employees
                </div>
              )}
              {formerProfiles().length === 0 &&
                profiles.filter((p) => (p as any).isFormerEmployee).length >
                  0 && (
                  <div
                    style={{
                      textAlign: "center",
                      fontSize: 12,
                      color: "#7a7d85",
                      fontStyle: "italic",
                      padding: "8px 0",
                    }}
                  >
                    No results match your search
                  </div>
                )}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                {formerProfiles().map((p) => {
                  const displayName =
                    (p as any).username ||
                    `${p.firstName}${p.lastName ? " " + p.lastName : ""}`;
                  const leftDate = (p as any).deletedAt
                    ? new Date((p as any).deletedAt).toLocaleDateString(
                        "en-GB",
                        { day: "2-digit", month: "short", year: "numeric" },
                      )
                    : "unknown date";
                  return (
                    <div key={p.id} style={{ minWidth: 190, maxWidth: 230 }}>
                      <div
                        className="prof-card"
                        style={{
                          opacity: 0.65,
                          filter: "grayscale(50%)",
                          outline: "1px solid rgba(224,82,82,0.3)",
                          cursor: "default",
                          pointerEvents: "none",
                        }}
                      >
                        <div className="prof-avatar">
                          {p.photo ? (
                            <img
                              src={p.photo}
                              className="prof-avatar-img"
                              alt=""
                            />
                          ) : (
                            <div
                              className="prof-avatar-initials"
                              style={{ color: "#7a7d85" }}
                            >
                              {(displayName || "?").charAt(0).toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div className="prof-info">
                          <div
                            className="prof-name"
                            style={{
                              textDecoration: "line-through",
                              color: "#7a7d85",
                            }}
                          >
                            {displayName}
                          </div>
                          <div
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                              background: "rgba(224,82,82,0.12)",
                              border: "1px solid rgba(224,82,82,0.35)",
                              borderRadius: 6,
                              padding: "3px 7px",
                              marginTop: 5,
                            }}
                          >
                            <svg
                              width="8"
                              height="8"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <circle
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="#e05252"
                                strokeWidth="2"
                              />
                              <path
                                d="M15 9l-6 6M9 9l6 6"
                                stroke="#e05252"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />
                            </svg>
                            <span
                              style={{
                                fontSize: 8,
                                fontWeight: 800,
                                color: "#e05252",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                              }}
                            >
                              No longer with the company
                            </span>
                          </div>
                          <div
                            style={{
                              fontSize: 10,
                              color: "#454850",
                              marginTop: 4,
                            }}
                          >
                            Left: {leftDate}
                          </div>
                          {p.dob && (
                            <div style={{ fontSize: 10, color: "#454850" }}>
                              DOB: {fmtDob(p.dob)}
                            </div>
                          )}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                        <button
                          onClick={() => {
                            updateProfile(p.id, (ex: any) => ({
                              ...ex,
                              isFormerEmployee: false,
                              deletedAt: null,
                            }));
                            showToast(
                              `${displayName} revived — back in active roster`,
                            );
                          }}
                          style={{
                            flex: 1,
                            padding: "7px 8px",
                            background: "rgba(62,207,110,0.1)",
                            border: "1px solid rgba(62,207,110,0.35)",
                            borderRadius: 7,
                            color: "#3ecf6e",
                            fontSize: 10,
                            fontWeight: 800,
                            textTransform: "uppercase",
                            cursor: "pointer",
                            fontFamily: "'Barlow',sans-serif",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 4,
                          }}
                        >
                          <svg
                            width="10"
                            height="10"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M23 4v6h-6M1 20v-6h6"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          Revive
                        </button>
                        <button
                          onClick={() => {
                            if (
                              !confirm(
                                `Permanently delete ${displayName}? This cannot be undone.`,
                              )
                            )
                              return;
                            removeProfile(p.id);
                            showToast(`${displayName} permanently deleted`);
                          }}
                          title="Permanently delete"
                          style={{
                            padding: "7px 10px",
                            background: "rgba(224,82,82,0.08)",
                            border: "1px solid rgba(224,82,82,0.3)",
                            borderRadius: 7,
                            color: "#e05252",
                            fontSize: 12,
                            cursor: "pointer",
                            fontFamily: "'Barlow',sans-serif",
                          }}
                        >
                          🗑
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            gap: 0,
            height: "calc(100vh - 240px)",
            overflow: "hidden",
          }}
        >
          {/* Drivers column */}
          <div
            style={{
              flex: 1,
              borderRight: "2px solid rgba(232,85,32,0.3)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "8px 14px",
                background: "rgba(232,85,32,0.07)",
                borderBottom: "1px solid rgba(232,85,32,0.2)",
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  color: "#E85520",
                }}
              >
                Drivers ({filteredProfiles("driver").length}/
                {profiles.filter((p) => p.role === "driver").length})
              </span>
            </div>
            <div
              style={{
                flex: 1,
                overflowY: "scroll",
                padding: "12px",
                display: "flex",
                flexWrap: "wrap",
                alignContent: "flex-start",
                gap: 12,
              }}
            >
              {profiles.filter((p) => p.role === "driver").length === 0 ? (
                <div className="prof-empty">No driver profiles yet.</div>
              ) : (
                filteredProfiles("driver").map((p) => (
                  <div
                    key={p.id}
                    className="prof-card"
                    onDoubleClick={() =>
                      selectMode ? toggleSelect(p.id) : setEditId(p.id)
                    }
                    onClick={() => selectMode && toggleSelect(p.id)}
                    style={{
                      outline: selected.has(p.id)
                        ? "2px solid #E85520"
                        : undefined,
                      cursor: selectMode ? "pointer" : undefined,
                    }}
                  >
                    <div className="prof-avatar">
                      {p.photo ? (
                        <img
                          src={p.photo}
                          className="prof-avatar-img"
                          alt={p.firstName}
                        />
                      ) : (
                        <div className="prof-avatar-initials">
                          {(p.firstName || "?").charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="prof-info">
                      <div className="prof-name">
                        {(p as any).username || (
                          <span style={{ color: "#e05252", fontSize: 11 }}>
                            No Username found
                          </span>
                        )}
                      </div>
                      <span
                        className={`prof-role-badge ${p.role === "driver" ? "role-driver" : "role-sideman"}`}
                      >
                        {p.role === "driver" ? "Driver" : "Sideman"}
                      </span>
                      {p.age && (
                        <div className="prof-age">User ID: {p.age}</div>
                      )}
                      {p.phone && <div className="prof-phone">{p.phone}</div>}
                      {p.dob && (
                        <div
                          className="prof-phone"
                          style={{ color: "var(--text-muted)", fontSize: 10 }}
                        >
                          DOB: {fmtDob(p.dob)}
                        </div>
                      )}
                    </div>
                    <div className="prof-actions">
                      <button
                        className="prof-action-btn prof-edit-btn"
                        onClick={() => setEditId(p.id)}
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                          <path
                            d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                      <button
                        className="prof-action-btn prof-del-btn"
                        title="Mark as former employee"
                        onClick={() => {
                          const name =
                            (p as any).username ||
                            `${p.firstName}${p.lastName ? " " + p.lastName : ""}`;
                          if (
                            !confirm(
                              `Mark ${name} as a former employee?\n\nThey'll be removed from active routes but all historical records remain intact.`,
                            )
                          )
                            return;
                          updateProfile(p.id, (ex: any) => ({
                            ...ex,
                            isFormerEmployee: true,
                            deletedAt: new Date().toISOString(),
                          }));
                          showToast(`${name} marked as former employee`);
                        }}
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Sidemen column */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "8px 14px",
                background: "rgba(62,207,110,0.07)",
                borderBottom: "1px solid rgba(62,207,110,0.2)",
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  color: "#3ecf6e",
                }}
              >
                Sidemen ({filteredProfiles("sideman").length}/
                {profiles.filter((p) => p.role === "sideman").length})
              </span>
            </div>
            <div
              style={{
                flex: 1,
                overflowY: "scroll",
                padding: "12px",
                display: "flex",
                flexWrap: "wrap",
                alignContent: "flex-start",
                gap: 12,
              }}
            >
              {profiles.filter((p) => p.role === "sideman").length === 0 ? (
                <div className="prof-empty">No sideman profiles yet.</div>
              ) : (
                filteredProfiles("sideman").map((p) => (
                  <div
                    key={p.id}
                    className="prof-card"
                    onDoubleClick={() =>
                      selectMode ? toggleSelect(p.id) : setEditId(p.id)
                    }
                    onClick={() => selectMode && toggleSelect(p.id)}
                    style={{
                      outline: selected.has(p.id)
                        ? "2px solid #E85520"
                        : undefined,
                      cursor: selectMode ? "pointer" : undefined,
                    }}
                  >
                    <div className="prof-avatar">
                      {p.photo ? (
                        <img
                          src={p.photo}
                          className="prof-avatar-img"
                          alt={p.firstName}
                        />
                      ) : (
                        <div className="prof-avatar-initials">
                          {(p.firstName || "?").charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="prof-info">
                      <div className="prof-name">
                        {(p as any).username || (
                          <span style={{ color: "#e05252", fontSize: 11 }}>
                            No Username found
                          </span>
                        )}
                      </div>
                      <span className="prof-role-badge role-sideman">
                        Sideman
                      </span>
                      {p.age && (
                        <div className="prof-age">User ID: {p.age}</div>
                      )}
                      {p.phone && <div className="prof-phone">{p.phone}</div>}
                      {p.dob && (
                        <div
                          className="prof-phone"
                          style={{ color: "var(--text-muted)", fontSize: 10 }}
                        >
                          DOB: {fmtDob(p.dob)}
                        </div>
                      )}
                    </div>
                    <div className="prof-actions">
                      <button
                        className="prof-action-btn prof-edit-btn"
                        onClick={() => setEditId(p.id)}
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                          <path
                            d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                      <button
                        className="prof-action-btn prof-del-btn"
                        title="Mark as former employee"
                        onClick={() => {
                          const name =
                            (p as any).username ||
                            `${p.firstName}${p.lastName ? " " + p.lastName : ""}`;
                          if (
                            !confirm(
                              `Mark ${name} as a former employee?\n\nThey'll be removed from active routes but all historical records remain intact.`,
                            )
                          )
                            return;
                          updateProfile(p.id, (ex: any) => ({
                            ...ex,
                            isFormerEmployee: true,
                            deletedAt: new Date().toISOString(),
                          }));
                          showToast(`${name} marked as former employee`);
                        }}
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {editId !== null && (
        <ProfileModal
          profileId={editId === "new" ? null : editId}
          onClose={() => setEditId(null)}
        />
      )}
    </>
  );
};

interface ProfileModalProps {
  profileId: string | null;
  onClose: () => void;
}

const ProfileModal: React.FC<ProfileModalProps> = ({ profileId, onClose }) => {
  const { profiles, addProfile, updateProfile } = useProfileStore();
  const existing = profileId ? profiles.find((p) => p.id === profileId) : null;

  const [firstName, setFirstName] = useState(existing?.firstName || "");
  const [lastName, setLastName] = useState(existing?.lastName || "");
  const [username, setUsername] = useState((existing as any)?.username || "");
  const [role, setRole] = useState<"driver" | "sideman">(
    existing?.role || "driver",
  );
  const normDob = (raw: string | undefined): string => {
    if (!raw) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    if (/^\d{2}[\/-]\d{2}[\/-]\d{4}$/.test(raw)) {
      const p = raw.split(/[\/-]/);
      return `${p[2]}-${p[1]}-${p[0]}`;
    }
    return raw;
  };
  const [dob, setDob] = useState(normDob(existing?.dob));
  const [age, setAge] = useState(existing?.age || "");
  const [phone, setPhone] = useState(existing?.phone || "");
  const [notes, setNotes] = useState(existing?.notes || "");
  const [photo, setPhoto] = useState<string | null>(existing?.photo || null);

  const handlePhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      showToast("Photo too large — max 2MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setPhoto(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const save = () => {
    if (!username.trim()) {
      showToast("Please enter a username");
      return;
    }
    if (!age.trim()) {
      showToast("User ID is required");
      return;
    }
    if (!dob) {
      showToast("Date of birth is required");
      return;
    }
    const p: Profile = {
      id: existing ? existing.id : generateId(),
      createdAt: existing ? existing.createdAt : new Date().toISOString(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      ...(username.trim() ? { username: username.trim() } : {}),
      role,
      dob,
      age: age || null,
      phone: phone || null,
      notes: notes || null,
      photo,
    } as any;
    if (existing) {
      updateProfile(existing.id, () => p);
      showToast("Profile updated");
    } else {
      addProfile(p);
      showToast("Profile saved");
    }
    onClose();
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.8)",
        zIndex: 8000,
        display: "flex",
        alignItems: "flex-end",
        backdropFilter: "blur(4px)",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#141518",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: "20px 20px 0 0",
          padding: "14px 20px 48px",
          width: "100%",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            width: 36,
            height: 4,
            background: "#24262c",
            borderRadius: 2,
            margin: "0 auto 16px",
          }}
        />
        <div className="prof-modal-title">
          {existing ? "Edit Profile" : "Add Profile"}
        </div>

        {/* Photo */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            marginBottom: 20,
          }}
        >
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              background: "#1c1e22",
              border: "2px solid rgba(255,255,255,0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 24,
              fontWeight: 700,
              color: "#E85520",
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            {photo ? (
              <img
                src={photo}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                alt=""
              />
            ) : (
              (firstName || "?").charAt(0).toUpperCase()
            )}
          </div>
          <div>
            <label
              style={{
                background: "#1c1e22",
                border: "1px dashed rgba(232,85,32,0.4)",
                borderRadius: 8,
                color: "#E85520",
                fontFamily: "'Barlow',sans-serif",
                fontSize: 12,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.3px",
                padding: "10px 14px",
                cursor: "pointer",
                display: "inline-block",
              }}
            >
              {photo ? "Change Photo" : "Upload Photo"} (optional)
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handlePhoto}
              />
            </label>
            <div style={{ fontSize: 11, color: "#454850", marginTop: 4 }}>
              JPG, PNG · Max 2MB
            </div>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 8,
            marginBottom: 14,
          }}
        >
          <div>
            <div className="form-label">
              First Name{" "}
              <span
                style={{
                  fontWeight: 400,
                  fontSize: 10,
                  color: "var(--text-muted)",
                }}
              >
                optional
              </span>
            </div>
            <input
              className="form-input"
              placeholder="First name (optional)"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </div>
          <div>
            <div className="form-label">
              Last Name{" "}
              <span
                style={{
                  fontWeight: 400,
                  fontSize: 10,
                  color: "var(--text-muted)",
                }}
              >
                optional
              </span>
            </div>
            <input
              className="form-input"
              placeholder="Last name (optional)"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <div className="form-label">
            Username <span style={{ color: "#e05252" }}>*</span>
          </div>
          <input
            className="form-input"
            placeholder="e.g. kirpal_driver"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 8,
            marginBottom: 14,
          }}
        >
          <div>
            <div className="form-label">
              User ID <span style={{ color: "#e05252" }}>*</span>
            </div>
            <input
              className="form-input"
              type="text"
              placeholder="e.g. D#001"
              value={age}
              onChange={(e) => setAge(e.target.value)}
            />
          </div>
          <div>
            <div className="form-label">
              Phone{" "}
              <span
                style={{
                  fontWeight: 400,
                  fontSize: 10,
                  color: "var(--text-muted)",
                }}
              >
                optional
              </span>
            </div>
            <input
              className="form-input"
              type="tel"
              placeholder="07..."
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
        </div>

        <div className="form-label" style={{ marginBottom: 8 }}>
          Role
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 8,
            marginBottom: 14,
          }}
        >
          {(["driver", "sideman"] as const).map((r) => (
            <div
              key={r}
              style={{
                padding: 12,
                background: role === r ? "rgba(232,85,32,0.1)" : "#1c1e22",
                border: `1px solid ${role === r ? "rgba(240,165,0,0.5)" : "rgba(255,255,255,0.07)"}`,
                borderRadius: 10,
                cursor: "pointer",
                textAlign: "center",
              }}
              onClick={() => setRole(r)}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.4px",
                  color: role === r ? "#E85520" : "#7a7d85",
                }}
              >
                {r === "driver" ? "Driver" : "Sideman"}
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginBottom: 14 }}>
          <div className="form-label" style={{ marginBottom: 7 }}>
            Date of Birth <span style={{ color: "#e05252" }}>*</span>
          </div>
          <input
            className="form-input"
            type="date"
            value={dob}
            onChange={(e) => setDob(e.target.value)}
          />
        </div>

        <div className="form-group">
          <div className="form-label">Notes (optional)</div>
          <input
            className="form-input"
            placeholder="Any notes…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        <button
          className="btn-primary"
          style={{ marginBottom: 8 }}
          onClick={save}
        >
          {existing ? "Save Changes" : "Save Profile"}
        </button>
        <button className="btn-secondary" onClick={onClose}>
          Cancel
        </button>
        {existing && (
          <button
            className="btn-secondary"
            style={{
              marginTop: 8,
              color: "#e05252",
              borderColor: "rgba(224,82,82,0.3)",
            }}
            onClick={() => {
              const displayName =
                (existing as any).username ||
                `${existing.firstName}${existing.lastName ? " " + existing.lastName : ""}`;
              if (
                !confirm(
                  `Archive ${displayName}?\n\nThey will be moved to "Employees No Longer With The Company". All historical records remain intact.`,
                )
              )
                return;
              useProfileStore
                .getState()
                .updateProfile(existing.id, (p: any) => ({
                  ...p,
                  isFormerEmployee: true,
                  deletedAt: new Date().toISOString(),
                }));
              showToast(`${displayName} archived`);
              onClose();
            }}
          >
            Archive this Employee
          </button>
        )}
      </div>
    </div>
  );
};

export default ProfileSection;
